import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/common";
import { PageError, PageLoading } from "@/components/LiveState";
import { useApp } from "@/context/AppContext";
import { ReadOnlyNotice } from "@/components/viewAs/ViewAs";
import { useLiveResource } from "@/hooks/useLiveResource";
import {
  AmountChoices,
  CreditsCard,
  EffortPanel,
  ModelUsagePanel,
  Notice,
  RoleNote,
  SharedAlloyPanel,
  SharedBillingNote,
  RoutingPanel,
  StatusChip,
  Switch,
  TopUpsPanel,
  TrustLine,
  TurnOnDialog,
  UsagePanel,
  packageChoiceLabel,
} from "@/components/alloy/AlloyParts";
import PeoplePanel from "@/components/alloy/PeoplePanel";
import {
  TURN_ON_WAIT_MS,
  actionErrorSentence,
  creditUnit,
  creditsPresentation,
  effortOptionLabel,
  effortOptions,
  forgetCheckout,
  formatTokens,
  markCheckoutPaid,
  modelChangeNote,
  pendingTurnOn,
  readCheckout,
  rememberCheckout,
  routingPresentation,
  runwayLine,
  turnOnStep,
} from "@/components/alloy/alloyView";

export {
  EFFORT_CHOICES,
  creditUnit,
  creditsPresentation,
  effortLabel,
  effortOptionLabel,
  effortOptions,
  formatTokens,
  formatUsd,
  modelChangeNote,
  modelLabel,
} from "@/components/alloy/alloyView";

const PENDING_ROUTE = ["queued", "applying"];
const PREFERRED_PACKAGE_CENTS = 5000;

/** Stripe sends the buyer back to /alloy?checkout=success|cancelled in this same tab. */
function readCheckoutReturn() {
  try {
    const value = new URLSearchParams(window.location.search || "").get("checkout");
    return value === "success" || value === "cancelled" ? value : null;
  } catch {
    return null;
  }
}

function clearCheckoutParam() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("checkout")) return;
    url.searchParams.delete("checkout");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // A reload would show the same notice again; nothing else depends on the parameter being gone.
  }
}

export default function Alloy() {
  const { api, activeClient, readOnly = false } = useApp();
  const resource = useLiveResource("getCredits");
  // Every workspace gets usage by person (since 2026-10-27). If the people route fails for any reason, the page
  // shows the model table on its own, exactly as it did before the panel.
  const people = useLiveResource("getAlloyPeople", [], { refreshMs: 300_000 });
  const peopleOn = people.status === "success" && Array.isArray(people.data?.people);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [chosenCents, setChosenCents] = useState(null);
  const [turnOnOpen, setTurnOnOpen] = useState(false);
  const [checkoutReturn] = useState(readCheckoutReturn);
  const [awaiting, setAwaiting] = useState(null);
  const autoRequested = useRef(false);
  const data = resource.data || null;
  const clientSlug = activeClient?.clientSlug ?? data?.clientSlug ?? null;
  const view = useMemo(() => creditsPresentation(data || {}), [data]);
  // Alloy tokens when the credits function sends a tokens block for this brand (AXOS first), dollars otherwise.
  const unit = useMemo(() => creditUnit(data || {}), [data]);
  const routing = useMemo(() => routingPresentation(data?.routing, data || {}), [data]);
  const canManage = ["owner", "admin"].includes(data?.role) && !readOnly;
  // A brand profile that shares its host brand's billing runs on the host's credits: nothing to buy or switch here,
  // and its own account's balance and setup are not what its agent uses.
  const sharedBilling = data?.hosting?.billingMode === "host";
  const sharedWith = sharedBilling ? (data.hosting.hostClientName ?? data.hosting.hostClientSlug) : null;
  const packages = data?.packages || [];
  const packageCents = chosenCents
    ?? (packages.some((entry) => entry.amountCents === PREFERRED_PACKAGE_CENTS) ? PREFERRED_PACKAGE_CENTS : packages[0]?.amountCents ?? null);
  const selectedPackage = packages.find((entry) => entry.amountCents === packageCents) || null;
  const routePending = PENDING_ROUTE.includes(data?.route?.status);
  const reload = resource.reload;

  const reloadQuietly = useCallback(async () => {
    try {
      await reload();
    } catch {
      // keep the last good data; a reload failure must not wedge the page
    }
  }, [reload]);

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (actionError) {
      setError(actionErrorSentence(actionError));
    } finally {
      await reloadQuietly();
      setBusy(false);
    }
  };

  // Coming back from Stripe, or reopening the page soon after paying to turn Alloy on: say what happened, and keep
  // waiting for the credits if this workspace asked for Alloy to be turned on.
  useEffect(() => {
    const now = Date.now();
    if (checkoutReturn) clearCheckoutParam();
    const record = readCheckout(now);
    const ours = !record || record.clientSlug === clientSlug;
    if (checkoutReturn === "cancelled") {
      if (record && ours) forgetCheckout();
      if (ours) setNotice({ tone: "info", text: "Payment cancelled. Nothing was charged." });
      return;
    }
    if (checkoutReturn === "success" && ours) {
      const paid = record ? markCheckoutPaid(clientSlug, now) : null;
      // Unit-neutral on purpose: this runs at mount, before the summary says whether the brand is on tokens.
      if (!paid?.turnOn) setNotice({ tone: "success", text: "Payment received. Your balance updates within a minute." });
    }
    const pending = pendingTurnOn(clientSlug, now);
    if (pending) {
      setAwaiting({ startedAt: pending.paidAt });
      setNotice({ tone: "info", text: "Payment received. Alloy turns on as soon as your payment is applied." });
    }
    // Runs once per mount: the checkout return this page was opened with, or a paid turn-on still in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a switch is in flight or credits are expected. The next poll is scheduled only once the last one has
  // settled: overlapping reloads discard each other's answers, so a slow summary would otherwise never update.
  const polling = routePending || Boolean(awaiting);
  useEffect(() => {
    if (!polling) return undefined;
    let stopped = false;
    let timer = null;
    const tick = async () => {
      try {
        await reload();
      } catch {
        // keep polling on the last good data
      }
      if (!stopped) timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [polling, reload]);

  const finishWaiting = useCallback((next) => {
    forgetCheckout();
    setAwaiting(null);
    if (next) setNotice(next);
  }, []);

  const stillApplying = "Your payment went through. It is still being applied; turn Alloy on once your balance shows.";

  useEffect(() => {
    if (!awaiting) return undefined;
    const remaining = Math.max(0, awaiting.startedAt + TURN_ON_WAIT_MS - Date.now()) + 1000;
    const timer = setTimeout(() => finishWaiting({ tone: "info", text: stillApplying }), remaining);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, finishWaiting]);

  useEffect(() => {
    if (!awaiting || !data || busy) return;
    const step = turnOnStep(data, awaiting.startedAt);
    if (step === "done") {
      finishWaiting({ tone: "success", text: data.alloyState === "on" ? "Payment received. Alloy is on." : "Payment received. Alloy is turning on." });
    } else if (step === "give-up") {
      finishWaiting({ tone: "info", text: stillApplying });
    } else if (step === "request" && !autoRequested.current) {
      autoRequested.current = true;
      (async () => {
        setBusy(true);
        try {
          await api.requestAlloyState({ requestedState: "on" });
          finishWaiting({ tone: "success", text: "Payment received. Alloy is turning on." });
        } catch (requestError) {
          if (requestError?.code === "credits_route_in_progress") {
            finishWaiting({ tone: "success", text: "Payment received. Alloy is turning on." });
          } else if (requestError?.code === "credits_balance_required") {
            // The credits have not landed on the key yet: let the next poll, at least five seconds out, try again.
            setTimeout(() => { autoRequested.current = false; }, 5000);
          } else {
            finishWaiting({ tone: "info", text: "Payment received, but Alloy couldn't be turned on automatically. Flip the switch to try again." });
            setError(actionErrorSentence(requestError));
          }
        } finally {
          await reloadQuietly();
          setBusy(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, data, busy, api, finishWaiting, reloadQuietly]);

  const startCheckout = (amountCents, { turnOn = false } = {}) => run(async () => {
    const result = await api.createCreditsCheckout({ amountCents });
    if (!String(result?.checkoutUrl || "").startsWith("https://checkout.stripe.com/")) throw new Error("Stripe did not return a secure checkout link.");
    rememberCheckout(clientSlug, { turnOn });
    // Same-tab navigation: `window.open` after an awaited call is popup-blocked by Safari/Firefox on this LIVE
    // payments page, silently no-op'ing the payment after the Stripe session was already created server-side.
    window.location.assign(result.checkoutUrl);
  });

  const onToggle = () => {
    if (!canManage || view.pending) return;
    if (view.on) {
      run(() => api.requestAlloyState({ requestedState: "off" }));
    } else if (view.needsPayment) {
      setTurnOnOpen(true);
    } else {
      run(() => api.requestAlloyState({ requestedState: "on" }));
    }
  };

  if (resource.status === "loading" && !data) return <PageLoading label="Loading GrowthOS Alloy" />;
  if (resource.status === "error" && !data) return <PageError error={resource.error} onRetry={resource.reload} />;

  const purchases = data?.purchases || [];
  const usage = data?.usage || { dailyUsd: 0, weeklyUsd: 0, monthlyUsd: 0 };
  const lastRechargeError = data?.autoRecharge?.lastError;

  return (
    <div data-testid="alloy-page">
      <PageHeader title="GrowthOS Alloy" subtitle={unit.tokens ? "Managed model access for your agent, paid for in Alloy tokens." : "Managed model access for your agent, billed from prepaid credits."}>
        <div className="flex flex-col items-start gap-2 md:items-end">
          <div className="flex items-center gap-3">
            {sharedBilling ? null : <StatusChip view={view} />}
            {sharedBilling ? null : (
              <Switch
                data-testid="alloy-toggle"
                checked={view.on}
                disabled={busy || !canManage || !view.canToggle || Boolean(awaiting)}
                label="GrowthOS Alloy"
                onClick={onToggle}
              />
            )}
          </div>
          {view.blocker && !sharedBilling ? <p data-testid="alloy-blocker" className="max-w-xs text-xs text-sm-ink/65 md:text-right">{view.blocker}</p> : null}
        </div>
      </PageHeader>

      <div className="space-y-6">
        <ReadOnlyNotice />
        {notice ? <Notice tone={notice.tone}>{notice.text}</Notice> : null}
        {data?.route?.status === "failed" ? <Notice tone="error" testId="alloy-route-error">The last change could not be applied. Try again or contact support.</Notice> : null}
        {error ? <Notice tone="error" testId="alloy-error">{error}</Notice> : null}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,468px)_minmax(0,1fr)] lg:items-start">
          <div className="flex min-w-0 flex-col gap-4">
            {sharedWith ? <SharedBillingNote hostName={sharedWith} /> : null}
            <CreditsCard
              balanceUsd={data?.balanceUsd}
              balance={unit.tokens ? formatTokens(data?.tokens?.balance) : null}
              unit={unit}
              runway={runwayLine(data)}
              view={view}
              workspaceName={activeClient?.clientName || "Your workspace"}
              autoRecharge={data?.autoRecharge}
              canManage={canManage}
              busy={busy}
              onToggleAutoRecharge={() => run(() => api.saveCreditsSettings({ autoRechargeEnabled: !data?.autoRecharge?.enabled, autoRechargePackageCents: data?.autoRecharge?.packageCents || 5000 }))}
              sharedWith={sharedWith}
            />
            {lastRechargeError && !sharedWith ? (
              <p className="px-1 text-xs text-red-700">Last auto-recharge failed: {String(lastRechargeError).replace(/^credits_stripe_/, "").replace(/_/g, " ")}.</p>
            ) : null}

            {sharedWith ? null : <EffortPanel
              model={data?.model}
              options={effortOptions(data?.model)}
              optionLabel={effortOptionLabel}
              canManage={canManage}
              busy={busy}
              alloyOn={data?.alloyState === "on"}
              modelText={data?.model?.overridden ? data.model.model : "Auto · recommended"}
              changeNote={modelChangeNote(data?.model)}
              unit={unit}
              onChoose={(value) => run(() => api.setAlloyModel({ reasoningEffort: value }))}
              onResetModel={() => run(() => api.setAlloyModel({ model: "auto", reasoningEffort: null }))}
            />}

            {sharedBilling ? null : <div className="flex flex-col gap-3" id="credits">
              <AmountChoices packages={packages} selectedCents={packageCents} onSelect={setChosenCents} disabled={busy || !canManage || !view.canAddCredits} unit={unit} />
              <button
                type="button"
                data-testid="credits-add"
                disabled={busy || !canManage || !view.canAddCredits || !selectedPackage}
                onClick={() => selectedPackage && startCheckout(selectedPackage.amountCents)}
                className="flex h-[46px] w-full items-center justify-center rounded-xl bg-sm-primary text-[15px] font-bold text-white shadow-[0_8px_18px_-10px_rgba(0,59,141,0.75)] transition hover:bg-sm-primary/90 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {selectedPackage ? `Top up ${packageChoiceLabel(selectedPackage, unit)}` : "Top up"}
              </button>
              <TrustLine unit={unit} />
            </div>}
            {sharedWith ? null : <RoleNote canManage={canManage} />}
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            {sharedWith ? <SharedAlloyPanel hostName={sharedWith} /> : <>
            <RoutingPanel routing={routing} />
            <UsagePanel usage={usage} unit={unit} onRefresh={() => { reload().catch(() => {}); }} refreshing={resource.status === "loading"} />
            {peopleOn ? (
              <PeoplePanel
                api={api}
                clientSlug={clientSlug}
                people={people.data.people}
                routing={data?.routing}
                configured={data?.configured}
                keyReady={data?.keyReady}
                unit={unit}
                onRefreshPeople={() => { people.reload().catch(() => {}); }}
              />
            ) : (
              <ModelUsagePanel routing={data?.routing} configured={data?.configured} keyReady={data?.keyReady} unit={unit} />
            )}
            <TopUpsPanel purchases={purchases} unit={unit} />
            </>}
          </div>
        </div>
      </div>

      <TurnOnDialog
        open={turnOnOpen}
        onOpenChange={setTurnOnOpen}
        packages={packages}
        selectedCents={packageCents}
        onSelect={setChosenCents}
        busy={busy}
        unit={unit}
        onContinue={(amountCents) => {
          setTurnOnOpen(false);
          startCheckout(amountCents, { turnOn: true });
        }}
      />
    </div>
  );
}
